import Async from "react-select/async";
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTranslatedWorkorderOriginLabel } from "../workorder-origin/WorkorderOriginDisplay";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

/**
* WOOriginSelect: Allow selection of workorder origins
* @param data - previous selected data
* @param onChange - on data change event
* @param className - additional classname
* @param isMulti - allow multiple selection
* @param placeholder - placeholder text for the select input
* @param classNamePrefix - prefix for react-select classNames
*/
export default function WOOriginSelect<T extends {label: string}>({
  data,
  onChange,
  className = "",
  isMulti = false,
  placeholder,
  classNamePrefix = "react-select",
  disabled
}: {
  data?: T | T[],
  onChange: (e: T | T[]) => void,
  className?: string,
  isMulti?: boolean,
  placeholder?: string,
  classNamePrefix?: string,
  disabled?: boolean
}) {
  const { t, i18n } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);

  const optionLabel = (el: T) => getTranslatedWorkorderOriginLabel(el as unknown as EasyTech.WorkorderOrigin);

  /**
  * useEffect: Transform value to be printed in select 
  * @param value - reload on value change
  */
  useEffect(() => {
    if (data) {
      if (isMulti) {
        const tmp = data as T[];
        setValueState(tmp.map(el => ({value: el, label: optionLabel(el)})));
      } else {
        const tmp = data as T;
        setValueState({value: tmp, label: optionLabel(tmp)});
      }
    }
    else setValueState(null);
  }, [data, isMulti, i18n.language]);
  /***/

  /**
  * getOriginList: get workorder origins list 
  * @return - printable list on select
  */
  const getOriginList = (inputValue?: string): Promise<EasyTech.IFormData<T>[]> => {
    return new Promise((resolve) => {
      let queries: { queries: Array<{ type: string; operator: string; field: string[]; value: string | undefined; }> } = {
        queries: [{
          type: "SEARCH",
          operator: "LIKE",
          field: ["label", "id"],
          value: inputValue,
        }]
      };

      if (!inputValue) { 
        queries = {queries: []};
      }

      api.post("users/workorder_origins/search", queries)
        .then((ret: any) => {
          const dataList = ret.data as T[] || ret as T[];
          return resolve(setSelectList<T>(dataList, optionLabel));
        })
        .catch(() => resolve([]));
    });
  };
  /***/

  /**
  * handleChange: manage change event
  * @param e - new value 
  */
  const handleChange = (e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[]) => {
    if (isMulti) {
      const result = (e as EasyTech.IFormData<T>[]).map((el) => el.value);
      onChange(result);
    } else {
      const result = (e as EasyTech.IFormData<T>).value;
      onChange(result);
    }
  };
  /***/

  const SelectField = isMulti ? MultiCheckSelect : Async;

  return (
    <SelectField   key={`${i18n.language}-${isMulti && data ? JSON.stringify((data as T[]).map(item => (item as any).id)) : "default"}`}
              cacheOptions
              defaultOptions
              loadOptions={(inputValue: string) => getOriginList(inputValue)}
              value={valueState}
              theme={reactSelectTheme}
              onChange={(e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[] | null) => handleChange(e as EasyTech.IFormData<T> | EasyTech.IFormData<T>[])}
              className={`w-full mr-2 ${className}`}
              name="workorder-origin"
              placeholder={placeholder || t("common:selectWorkorderOrigin")}
              classNamePrefix={classNamePrefix}
              isMulti={isMulti}
              closeMenuOnSelect={!isMulti}
              hideSelectedOptions={!isMulti}
              isClearable={false}
              isDisabled={disabled} />
  );
}
/***/
